import { useEffect, useMemo, useState } from 'react'

import { TreeSelectItemOption } from '@/components'
import { WholesaleMarketDatasetWithFilesSimple } from '@/interfaces'

import { MarketParticipationFilters } from '../types'
import { useIsSpainDataset } from './useIsSpainDataset'

export const useMarketParticipationFilters = (
  datasetInfo?: WholesaleMarketDatasetWithFilesSimple
) => {
  const [marketParticipationFilters, setMarketParticipationFilters] =
    useState<MarketParticipationFilters>({
      hasCapacityMarketPrice: false,
      hasRegulationMileage: false,
      hasSpinningReserve: false,
      hasRegulationDown: false,
      hasRegulationUp: false,
      hasRealTimeEnergy: false,
      hasDayAheadEnergy: false,
      hasReeDayAheadEnergy: false,
      hasEnergyDownAFRR: false,
      hasEnergyDownMFRR: false,
      hasEnergyUpAFRR: false,
      hasEnergyUpMFRR: false,
      hasPowerAFRR: false
    })

  const isSpainMarket = useIsSpainDataset(datasetInfo)

  useEffect(() => {
    setMarketParticipationFilters(currentValue => ({
      hasCapacityMarketPrice:
        currentValue.hasCapacityMarketPrice ||
        !!datasetInfo?.capacityMarketPriceFile?.id,
      hasRegulationMileage:
        currentValue.hasRegulationMileage ||
        !!datasetInfo?.regulationMileagePriceFile?.id,
      hasSpinningReserve:
        currentValue.hasSpinningReserve ||
        !!datasetInfo?.spinningReservePricesFile?.id,
      hasRegulationDown:
        currentValue.hasRegulationDown || !!datasetInfo?.regDownPricesFile?.id,
      hasRegulationUp:
        currentValue.hasRegulationUp || !!datasetInfo?.regUpPricesFile?.id,
      hasRealTimeEnergy:
        currentValue.hasRealTimeEnergy ||
        !!datasetInfo?.realTimeEnergyPriceFile?.id,
      hasDayAheadEnergy:
        currentValue.hasDayAheadEnergy ||
        !!datasetInfo?.dayAheadEnergyPricesFile?.id,
      hasReeDayAheadEnergy: isSpainMarket,
      hasEnergyDownAFRR: isSpainMarket,
      hasEnergyDownMFRR: isSpainMarket,
      hasEnergyUpAFRR: isSpainMarket,
      hasEnergyUpMFRR: isSpainMarket,
      hasPowerAFRR: isSpainMarket
    }))
  }, [datasetInfo, isSpainMarket])

  const handleChange = (val: Partial<MarketParticipationFilters>) => {
    setMarketParticipationFilters(currentValue => ({ ...currentValue, ...val }))
  }

  const items = useMemo(
    () =>
      isSpainMarket
        ? ([
            {
              label: 'Energy',
              isDisabled: true,
              children: [
                {
                  label: 'REE Day-Ahead Energy Market Prices',
                  checked: marketParticipationFilters.hasReeDayAheadEnergy,
                  isDisabled: true
                },
                {
                  label: 'Energy Down (aFRR)',
                  checked: marketParticipationFilters.hasEnergyDownAFRR,
                  isDisabled: true
                },
                {
                  label: 'Energy Up (aFRR)',
                  checked: marketParticipationFilters.hasEnergyUpAFRR,
                  isDisabled: true
                },
                {
                  label: 'Power (aFRR)',
                  checked: marketParticipationFilters.hasPowerAFRR,
                  isDisabled: true
                },
                {
                  label: 'Energy Down (mFRR)',
                  checked: marketParticipationFilters.hasEnergyDownMFRR,
                  isDisabled: true
                },
                {
                  label: 'Energy Up (mFRR)',
                  checked: marketParticipationFilters.hasEnergyUpMFRR,
                  isDisabled: true
                }
              ]
            }
          ] as TreeSelectItemOption[])
        : ([
            {
              label: 'Energy',
              isDisabled:
                !!datasetInfo?.dayAheadEnergyPricesFile?.id ||
                !!datasetInfo?.realTimeEnergyPriceFile?.id,
              onChange: val =>
                handleChange({
                  hasDayAheadEnergy: val,
                  hasRealTimeEnergy: val
                }),
              children: [
                {
                  label: 'Day-Ahead Energy',
                  checked: marketParticipationFilters?.hasDayAheadEnergy,
                  isDisabled: !!datasetInfo?.dayAheadEnergyPricesFile?.id,
                  onChange: val => handleChange({ hasDayAheadEnergy: val })
                },
                {
                  label: 'Real Time Energy',
                  checked: marketParticipationFilters?.hasRealTimeEnergy,
                  isDisabled: !!datasetInfo?.realTimeEnergyPriceFile?.id,
                  onChange: val => handleChange({ hasRealTimeEnergy: val })
                }
              ]
            },
            {
              label: 'Ancillary Service',
              isDisabled:
                !!datasetInfo?.regUpPricesFile?.id ||
                !!datasetInfo?.regDownPricesFile?.id ||
                !!datasetInfo?.spinningReservePricesFile?.id ||
                !!datasetInfo?.regulationMileagePriceFile?.id,
              onChange: val =>
                handleChange({
                  hasRegulationUp: val,
                  hasRegulationDown: val,
                  hasSpinningReserve: val,
                  hasRegulationMileage: val
                }),
              children: [
                {
                  label: 'Regulation Up',
                  checked: marketParticipationFilters?.hasRegulationUp,
                  isDisabled: !!datasetInfo?.regUpPricesFile?.id,
                  onChange: val => handleChange({ hasRegulationUp: val })
                },
                {
                  label: 'Regulation Down',
                  checked: marketParticipationFilters?.hasRegulationDown,
                  isDisabled: !!datasetInfo?.regDownPricesFile?.id,
                  onChange: val => handleChange({ hasRegulationDown: val })
                },
                {
                  label: 'Spinning Reserve',
                  checked: marketParticipationFilters?.hasSpinningReserve,
                  isDisabled: !!datasetInfo?.spinningReservePricesFile?.id,
                  onChange: val => handleChange({ hasSpinningReserve: val })
                },
                {
                  label: 'Regulation Mileage',
                  checked: marketParticipationFilters?.hasRegulationMileage,
                  isDisabled: !!datasetInfo?.regulationMileagePriceFile?.id,
                  onChange: val => handleChange({ hasRegulationMileage: val })
                }
              ]
            },
            {
              label: 'Capacity Market Price',
              checked: marketParticipationFilters?.hasCapacityMarketPrice,
              isDisabled: !!datasetInfo?.capacityMarketPriceFile?.id,
              onChange: val => handleChange({ hasCapacityMarketPrice: val })
            }
          ] as TreeSelectItemOption[]),
    [datasetInfo, isSpainMarket, marketParticipationFilters]
  )

  // TODO: Fix that - temporary always `isDatasetValid: true`
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isDatasetValid = useMemo(() => {
    const pairs = isSpainMarket
      ? [
          {
            isChecked: marketParticipationFilters.hasReeDayAheadEnergy,
            isFileUploaded: !!datasetInfo?.reeDayAheadEnergyPricesFile?.id
          },
          {
            isChecked: marketParticipationFilters.hasEnergyDownAFRR,
            isFileUploaded: !!datasetInfo?.afrrEnergyDownFile?.id
          },
          {
            isChecked: marketParticipationFilters.hasEnergyUpAFRR,
            isFileUploaded: !!datasetInfo?.afrrEnergyUpFile?.id
          },
          {
            isChecked: marketParticipationFilters.hasPowerAFRR,
            isFileUploaded: !!datasetInfo?.afrrPowerFile?.id
          },
          {
            isChecked: marketParticipationFilters.hasEnergyDownMFRR,
            isFileUploaded: !!datasetInfo?.mfrrEnergyDownFile?.id
          },
          {
            isChecked: marketParticipationFilters.hasEnergyUpMFRR,
            isFileUploaded: !!datasetInfo?.mfrrEnergyUpFile?.id
          }
        ]
      : [
          {
            isChecked: marketParticipationFilters.hasDayAheadEnergy,
            isFileUploaded: !!datasetInfo?.dayAheadEnergyPricesFile?.id
          },
          {
            isChecked: marketParticipationFilters.hasRealTimeEnergy,
            isFileUploaded: !!datasetInfo?.realTimeEnergyPriceFile?.id
          },
          {
            isChecked: marketParticipationFilters.hasRegulationUp,
            isFileUploaded: !!datasetInfo?.regUpPricesFile?.id
          },
          {
            isChecked: marketParticipationFilters.hasRegulationDown,
            isFileUploaded: !!datasetInfo?.regDownPricesFile?.id
          },
          {
            isChecked: marketParticipationFilters.hasSpinningReserve,
            isFileUploaded: !!datasetInfo?.spinningReservePricesFile?.id
          },
          {
            isChecked: marketParticipationFilters.hasRegulationMileage,
            isFileUploaded: !!datasetInfo?.regulationMileagePriceFile?.id
          },
          {
            isChecked: marketParticipationFilters.hasCapacityMarketPrice,
            isFileUploaded: !!datasetInfo?.capacityMarketPriceFile?.id
          }
        ]

    if (pairs.some(x => x.isChecked && !x.isFileUploaded)) return false

    return pairs.some(x => x.isChecked)
  }, [datasetInfo, isSpainMarket, marketParticipationFilters])

  return {
    treeSelectItems: items,
    filters: marketParticipationFilters,
    isDatasetValid: true
  }
}
